import React, { useState, ReactNode } from 'react';
import { Layout, Button, Typography, Card, Space, Divider } from 'antd';
import { useDarkMode } from 'src/context/DarkModeContext';
import { QuizQuestion } from 'src/data/models/quiz/QuizQuestionModel';
import QuizProgressBar from 'src/components/ProgressBar';
import { QuizQuestionAnswer } from 'src/data/models/quiz/QuizQuestionResultModel';
import './QuizContents.scss';

const { Title, Text } = Typography;

interface QuizContentsScreenProps {
    quizQuestions: QuizQuestion[];
    recordAnswer: (answer: QuizQuestionAnswer) => void;
    endQuiz: () => void;
}

const QuizContents: React.FC<QuizContentsScreenProps> = ({
    quizQuestions,
    recordAnswer,
    endQuiz,
}) => {
    const { isDarkMode } = useDarkMode();

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

    const handleAnswerSelected = (answerIndex: number) => {
        if (selectedAnswer !== null) return;

        setSelectedAnswer(answerIndex);
        const current = quizQuestions[currentQuestionIndex];

        const answer = new QuizQuestionAnswer(
            current,
            current.correctAnswerIndex,
            answerIndex
        );
        recordAnswer(answer);
    };

    const handleNextQuestion = () => {
        if (selectedAnswer === null) return;

        if (currentQuestionIndex < quizQuestions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
            setSelectedAnswer(null);
        } else {
            endQuiz();
        }
    };

    const renderExplanationIfNeeded = (
        selectedAnswer: number | null,
        current: QuizQuestion
    ): ReactNode => {
        const showExplanation =
            selectedAnswer !== null &&
            selectedAnswer !== current.correctAnswerIndex &&
            current.explanation;

        return (
            showExplanation && (
                <Card>
                    <Text type="warning" strong>
                        Explanation:
                    </Text>
                    <p>{current.explanation}</p>
                </Card>
            )
        );
    };

    if (quizQuestions.length === 0) {
        return (
            <Layout>
                <Title level={3}>
                    No questions found for the selected categories.
                </Title>
            </Layout>
        );
    }

    const current = quizQuestions[currentQuestionIndex];

    return (
        <div className={`quiz-container ${isDarkMode ? 'dark' : 'light'}`}>
            <Space id="quiz-vertical-space" direction="vertical">
                <QuizProgressBar
                    questionNumber={currentQuestionIndex}
                    totalQuestionNumber={quizQuestions.length}
                />

                <Card id="question-card">
                    <Text>{current.questionText}</Text>
                    <Divider />
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {current.options.map((option, index) => {
                            const isCorrect = index === current.correctAnswerIndex;
                            const isSelected = selectedAnswer === index;
                            const isWrongSelected = isSelected && !isCorrect;

                            return (
                                <Button
                                    key={index}
                                    type="default"
                                    onClick={() => handleAnswerSelected(index)}
                                    disabled={selectedAnswer !== null}
                                    className={`
                      answer-btn 
                      ${isCorrect && selectedAnswer !== null ? 'correct' : ''} 
                      ${isWrongSelected ? 'incorrect' : ''}
                    `}
                                    block
                                >
                                    {option}
                                </Button>
                            );
                        })}
                    </Space>
                </Card>

                {renderExplanationIfNeeded(selectedAnswer, current)}

                <Button
                    type="primary"
                    onClick={handleNextQuestion}
                    disabled={selectedAnswer === null}
                    block
                >
                    {currentQuestionIndex < quizQuestions.length - 1
                        ? 'Next Question'
                        : 'Finish Quiz'}
                </Button>
            </Space>
        </div>
    );
};

export default QuizContents;
